import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Typography,
  useTheme,
  Tooltip,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { ReactComponent as DeleteOutlineRoundedIcon } from '../assets/icons/main/delete-icon.svg';
import { ReactComponent as KeyboardArrowDownIcon } from '../assets/icons/main/expand-down-icon.svg';
import { ReactComponent as KeyboardArrowUpIcon } from '../assets/icons/main/collapse-up-icon.svg';
import CustomDataTable from '../components/CustomDataTable';
import SelectComponent from '../components/Select';
import SearchField from './searchField';
import { cloneDeep, get, isEqual, set, uniq, uniqBy } from 'lodash';
import { MessageIssue, collectMessageValidationIssues } from './flowImportMessageValidationUtils';
import JsonFormsWrapper from './FlowRenderer/react-flow/components/JsonFormsWrapper';
import { PropIssuesContext } from './FlowRenderer/react-flow/custom-renderers/cellPropTextField';
import { palette } from '../theme/palette';
import PopUp from '../components/Popup';
import { ReactComponent as CrossIcon } from '../assets/icons/main/alertIcon.svg';
import { ReactComponent as AlertIcon } from '../assets/icons/alert-small-icon.svg';
const Styles = {
  dialog: {
    muiPaper: {
      maxWidth: '800px',
      // padding: '21px 32px 32px',
      width: '800px',
      borderRadius: '16px',
    },
    // '& .MuiPaper-root:not(.MuiAccordion-root)': {
    // maxWidth: '800px',
    // padding: '21px 32px',
    // width: '800px',
    // borderRadius: '18px',
    //   backgroundColor: `${palette.transparent.main}`,
    // },
    // '&.MuiPaper-root.MuiDialog-root': {
    //   maxWidth: '800px',
    //   padding: '21px 32px',
    //   width: '800px',
    //   borderRadius: '18px',
    //   backgroundColor: `${palette.transparent.main}`,
    // },
  },
  dialogTitle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '1.3rem',
    padding: '0px',
    fontFamily: 'Open Sans',
    fontWeight: '400',
  },
  content: {
    '&.MuiDialogContent-root': {
      padding: '0px',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '0px',
      gap: '0px',
      flex: 0
    },
  },
  closeIcon: {
    cursor: 'pointer',
  },
};

const STATUS_OPTIONS = [
  { value: 'Enable', label: 'Enable' },
  { value: 'Disable', label: 'Disable' },
];

const TABLE_ROW_HEIGHT = 78;

const cellSelectSx = {
  borderRadius: '8px',
  fontSize: '12px',
  backgroundColor: '#FFFFFF',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#D9DFED',
  },
  '& .MuiSelect-select': {
    display: 'flex',
  },
};

const helperTextSx = (error = false) => ({
  minHeight: '16px',
  lineHeight: 1.2,
  fontSize: '0.75rem',
  fontWeight: 400,
  color: error ? palette.error.main : palette.warning.main,
  marginTop: '2px',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
});

const CellStack = ({
  children,
  helperText,
  helperError = false,
}: {
  children: React.ReactNode;
  helperText?: string;
  helperError?: boolean;
}) => {
  const helper = String(helperText || '');

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.25,
        py: 0.5,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>{children}</Box>

      <Tooltip
        title={helper}
        placement='right'
        arrow
        disableHoverListener={!helper.trim()}
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 200,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          },
        }}
      >
        <Typography variant='caption' sx={helperTextSx(helperError)}>
          {helper || ' '}
        </Typography>
      </Tooltip>
    </Box>
  );
};

type ValidationTextCellProps = {
  value: string;
  error?: boolean;
  warning?: boolean;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
  helperError?: boolean;
  onChange: (value: string) => void;
};

const ValidationTextCell = memo(
  ({
    value,
    error = false,
    warning = false,
    disabled = false,
    placeholder = '',
    helperText = '',
    helperError = false,
    onChange,
  }: ValidationTextCellProps) => {
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value), [onChange]);

    const borderColor = error
      ? palette.error.main
      : warning
        ? palette.warning.main
        : palette.secondary.main;

    return (
      <CellStack helperText={helperText} helperError={helperError}>
        <TextField
          value={value}
          variant='outlined'
          size='small'
          fullWidth
          disabled={disabled}
          placeholder={placeholder}
          error={error}
          sx={{
            '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              borderColor: `${borderColor} !important`,
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: `${borderColor} !important`,
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `${borderColor} !important`,
            },
          }}
          onChange={handleChange}
        />
      </CellStack>
    );
  }
);

ValidationTextCell.displayName = 'ValidationTextCell';

type ValidationSelectCellProps = {
  value: string;
  options: { label: string; value: string }[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  warning?: boolean;
  helperText?: string;
  helperError?: boolean;
  onChange: (value: string) => void;
};

const ValidationSelectCell = memo(
  ({
    value,
    options,
    placeholder,
    disabled = false,
    error = false,
    warning = false,
    helperText = '',
    helperError = false,
    onChange,
  }: ValidationSelectCellProps) => {
    const handleChange = useCallback((e: any) => onChange(String(e.target.value || '')), [onChange]);

    const borderColor = error
      ? palette.error.main
      : warning
        ? palette.warning.main
        : palette.secondary.main;

    return (
      <CellStack helperText={helperText} helperError={helperError}>
        <SelectComponent
          value={value}
          options={options}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          sx={{
            ...cellSelectSx,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: `${borderColor} !important`,
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: `${borderColor} !important`,
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: `${borderColor} !important`,
            },
          }}
          onChange={handleChange}
        />
      </CellStack>
    );
  }
);

ValidationSelectCell.displayName = 'ValidationSelectCell';

type SecretFieldDef = {
  key: string;
  label: string;
  isDropdown?: boolean;
  options?: string[];
};

type FlowImportMessageValidationModalProps = {
  open: boolean;
  selectedGatewayName: string;
  assets: any[];
  failedAssetIndexes: number[];
  validationRows: any[];
  secretsData: any[];
  componentSecretFieldsMap: Record<string, SecretFieldDef[]>;
  componentSchemaMap: Record<string, { ui_schema?: any; backend_schema?: any }>;
  onBack: () => void;
  onClose: () => void;
  onContinue: (assets: any[]) => void;
};

type MessageGridRow = {
  id: string;
  assetIndex: number;
  componentIndex: number;
  assetName: string;
  assetIp: string;
  assetStatus: string;
  componentName: string;
  componentStatus: string;
  secrets: Record<string, string>;
};

type SortState = {
  field: string;
  sort: '' | 'asc' | 'desc';
};

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const normalizeText = (value: any) => String(value ?? '').trim();
const normalizeKey = (value: any) => normalizeText(value).toLowerCase();

const toTitle = (value: string) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const scopeToKey = (scope: string) =>
  String(scope || '')
    .replace(/^#\/properties\//, '')
    .replace(/^#\//, '')
    .split('/')
    .pop() || '';

const getMessagesControl = (uiSchema: any) => {
  const elements = Array.isArray(get(uiSchema, 'elements')) ? get(uiSchema, 'elements') : [];
  return elements.find((element: any) => scopeToKey(get(element, 'scope', '')) === 'messages') || null;
};

const getMessageLabels = (messagesControl: any) => {
  const detailElements = Array.isArray(get(messagesControl, 'options.detail.elements'))
    ? get(messagesControl, 'options.detail.elements')
    : [];
  const labels: Record<string, string> = {};
  detailElements.forEach((el: any) => {
    const key = scopeToKey(get(el, 'scope', ''));
    if (key) {
      labels[key] = get(el, 'label', toTitle(key));
    }
  });
  return labels;
};

const applyReadonlyToMessageHeaderFields = (uiSchemaInput: any) => {
  const nextUiSchema = cloneDeep(uiSchemaInput || {});

  const markReadonly = (node: any) => {
    if (!node || typeof node !== 'object') return;

    if (node.type === 'Control') {
      const key = scopeToKey(get(node, 'scope', ''));
      if (key === 'id' || key === 'name' || key === 'type') {
        node.options = {
          ...(node.options || {}),
          readonly: true,
        };
      }
    }

    const elements = Array.isArray(node.elements) ? node.elements : [];
    elements.forEach(markReadonly);

    if (node.options?.detail?.elements) {
      const detailElements = Array.isArray(node.options.detail.elements) ? node.options.detail.elements : [];
      detailElements.forEach(markReadonly);
    }
  };

  markReadonly(nextUiSchema);
  return nextUiSchema;
};

const getSchemaDisplayValue = (rawValue: any, fieldSchema: any) => {
  const value = rawValue === undefined || rawValue === null ? '' : String(rawValue);
  const oneOf = Array.isArray(get(fieldSchema, 'oneOf')) ? get(fieldSchema, 'oneOf') : [];
  const anyOf = Array.isArray(get(fieldSchema, 'anyOf')) ? get(fieldSchema, 'anyOf') : [];
  const variants = oneOf.length > 0 ? oneOf : anyOf;
  if (variants.length > 0) {
    const matched = variants.find((item: any) => String(get(item, 'const', '')) === value);
    if (matched) {
      return String(get(matched, 'title', value));
    }
  }

  const enumValues = Array.isArray(get(fieldSchema, 'enum')) ? get(fieldSchema, 'enum') : [];
  const enumTitles =
    (Array.isArray(get(fieldSchema, 'enumNames')) && get(fieldSchema, 'enumNames')) ||
    (Array.isArray(get(fieldSchema, 'x-enumNames')) && get(fieldSchema, 'x-enumNames')) ||
    [];
  const idx = enumValues.findIndex((item: any) => String(item) === value);
  if (idx >= 0 && enumTitles[idx]) {
    return String(enumTitles[idx]);
  }

  return value;
};

const coerceBySchemaType = (rawValue: any, fieldSchema: any) => {
  const schemaTypeRaw = get(fieldSchema, 'type');
  const schemaType = Array.isArray(schemaTypeRaw)
    ? schemaTypeRaw.find((t: any) => t && t !== 'null')
    : schemaTypeRaw;

  if (rawValue === undefined || rawValue === null) return rawValue;

  if (schemaType === 'number' || schemaType === 'integer') {
    if (typeof rawValue === 'number' && Number.isFinite(rawValue)) return rawValue;
    const rawText = String(rawValue).trim();
    if (!rawText) return rawValue;
    const parsed = Number(rawText);
    if (!Number.isFinite(parsed)) return rawValue;
    if (schemaType === 'integer' && !Number.isInteger(parsed)) return rawValue;
    return parsed;
  }

  if (schemaType === 'boolean') {
    if (typeof rawValue === 'boolean') return rawValue;
    const normalized = String(rawValue).trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
    return rawValue;
  }

  return rawValue;
};

const normalizeMessagesForOption = (option: any, messageProperties: Record<string, any>, propsProperties: Record<string, any>) => {
  const rootMessages = Array.isArray(get(option, 'messages')) ? get(option, 'messages') : [];
  const dataMessages = Array.isArray(get(option, 'data.messages')) ? get(option, 'data.messages') : [];
  const sourceMessages = dataMessages.length >= rootMessages.length ? dataMessages : rootMessages;

  const nextMessages = (sourceMessages || []).map((message: any) => {
    const nextMessage = { ...(message || {}) };

    // Message Validation UI commonly binds "Message Name" to messages[].id.
    const normalizedName = String(get(nextMessage, 'name', '')).trim();
    if (normalizedName) {
      nextMessage.id = normalizedName;
      nextMessage.name = normalizedName;
    }

    Object.keys(messageProperties || {}).forEach((fieldKey) => {
      if (fieldKey === 'props') return;
      nextMessage[fieldKey] = coerceBySchemaType(get(nextMessage, fieldKey), messageProperties[fieldKey]);
    });

    if (Array.isArray(nextMessage.props)) {
      nextMessage.props = nextMessage.props.map((prop: any) => {
        const nextProp = { ...(prop || {}) };
        Object.keys(propsProperties || {}).forEach((fieldKey) => {
          nextProp[fieldKey] = coerceBySchemaType(get(nextProp, fieldKey), propsProperties[fieldKey]);
        });
        return nextProp;
      });
    }

    return nextMessage;
  });

  if (!option.data || typeof option.data !== 'object') {
    option.data = {};
  }
  option.data.messages = nextMessages;

  return nextMessages;
};

function findObjectsWithKey(obj: any, key: any, path: any = []) {
  let results: any = [];
  if (typeof obj === 'object' && obj !== null) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      results.push({ path: [...path], object: obj });
    }
    for (const k in obj) {
      results = results.concat(findObjectsWithKey(obj[k], key, [...path, k]));
    }
  }
  return results;
}

const applyScanResultToSchema = (schemaInput: any, scanResultInput: any[]) => {
  const schema = cloneDeep(schemaInput || {});
  const scanResult = Array.isArray(scanResultInput) ? scanResultInput : [];
  if (!scanResult.length || !schema || typeof schema !== 'object') return schema;

  const collectScanTagCandidates = () => {
    const values = new Set<any>();

    scanResult.forEach((tag: any) => {
      if (tag === null || tag === undefined) return;

      if (typeof tag === 'string' || typeof tag === 'number' || typeof tag === 'boolean') {
        values.add(tag);
        return;
      }

      if (typeof tag !== 'object') return;

      [
        'id',
        // 'ID',
        // 'nodeId',
        // 'node_id',
        // 'address',
        // 'name',
        // 'Name',
        // 'key',
        // 'displayName',
      ].forEach((key) => {
        const next = tag?.[key];
        if (next !== undefined && next !== null && String(next).trim() !== '') {
          values.add(next);
        }
      });
    });

    const configuredColumns = Array.isArray(get(schema, 'scanResult.columns'))
      ? get(schema, 'scanResult.columns')
      : [];

    configuredColumns.forEach((col: any) => {
      const key = String(get(col, 'value', '')).trim();
      if (!key) return;
      scanResult.forEach((tag: any) => {
        if (!tag || typeof tag !== 'object') return;
        const next = tag[key];
        if (next !== undefined && next !== null && String(next).trim() !== '') {
          values.add(next);
        }
      });
    });

    return uniq(Array.from(values).filter((value: any) => value !== undefined && value !== null && String(value).trim() !== ''));
  };

  const scanTagEnumValues = collectScanTagCandidates();

  if (get(schema, 'scanResult.scope')) {
    const dotPath = String(get(schema, 'scanResult.scope', '')).replace(/^#\//, '').replace(/\//g, '.');
    const isOneOf = get(schema, 'scanResult.oneOf');
    if (isOneOf) {
      const titleKey = get(schema, 'scanResult.columns[0].value', 'id');
      const constKey = get(schema, 'scanResult.columns[1].value', 'id');
      set(
        schema,
        `${dotPath}.oneOf`,
        uniqBy(scanResult, constKey).map((tag: any) => ({
          title: tag?.[titleKey],
          const: tag?.[constKey],
        }))
      );
    } else {
      const key = get(schema, 'scanResult.columns[0].value', 'id');
      set(
        schema,
        `${dotPath}.enum`,
        uniq(scanResult.map((tag: any) => tag?.[key]).filter((value: any) => value !== undefined && value !== null))
      );
    }
    // Do not return here: we also enforce fallback trigger/parameter enums below.
  }

  const results = findObjectsWithKey(schema, 'scanResults');
  if (results && results.length > 0) {
    results.forEach((result: any) => {
      const enumData = uniq(
        scanResult
          .map((tag: any) => (result.object.scanResultsKey ? tag?.[result.object.scanResultsKey] : tag?.id))
          .filter((value: any) => value !== undefined && value !== null)
      );
      set(schema, [...result.path, 'enum'], enumData);
    });
    // Do not return here: we also enforce fallback trigger/parameter enums below.
  }

  const uniqueTagIds = scanTagEnumValues;
  const messagesProps = get(schema, 'properties.messages.items.properties', {});

  // Inject scan enums into fields marked x-scanValidate:true; fallback to known defaults.
  const messageScanFields = Object.keys(messagesProps).filter(
    (key) => key !== 'props' && get(messagesProps[key], 'x-scanValidate') === true
  );
  (messageScanFields.length > 0 ? messageScanFields : ['trigger']).forEach((fieldKey) => {
    if (messagesProps[fieldKey]) messagesProps[fieldKey].enum = uniqueTagIds;
  });

  const paramProps = get(messagesProps, 'props.items.properties', {}) as Record<string, any>;
  const paramScanFields = Object.keys(paramProps).filter(
    (key) => get(paramProps[key], 'x-scanValidate') === true
  );
  (paramScanFields.length > 0 ? paramScanFields : ['id']).forEach((fieldKey) => {
    if (paramProps[fieldKey]) paramProps[fieldKey].enum = uniqueTagIds;
  });

  return schema;
};
const noWrapHeader = (label: string) => <Box component='span' sx={{ whiteSpace: 'nowrap' }}>{label}</Box>;

const FlowImportMessageValidationModal = ({
  open,
  selectedGatewayName,
  assets,
  failedAssetIndexes,
  validationRows,
  secretsData,
  componentSecretFieldsMap,
  componentSchemaMap,
  onBack,
  onClose,
  onContinue,
}: FlowImportMessageValidationModalProps) => {
  const theme = useTheme();
  const [draftAssets, setDraftAssets] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortState, setSortState] = useState<SortState>({ field: '', sort: '' });
  const [expandedRows, setExpandedRows] = useState<Array<string | number>>([]);
  const [expandedMessageKeys, setExpandedMessageKeys] = useState<Record<string, boolean>>({});
  const [jsonFormErrorsByKey, setJsonFormErrorsByKey] = useState<Record<string, any[]>>({});
  const [formSessionKey, setFormSessionKey] = useState(0);
  const [pendingDeleteMessage, setPendingDeleteMessage] = useState<{ assetIndex: number; componentIndex: number; messageIndex: number } | null>(null);
  const PAGE_SIZE = 7;

  const failedAssetIndexSet = useMemo(
    () => new Set((failedAssetIndexes || []).map((idx) => Number(idx))),
    [failedAssetIndexes]
  );

  useEffect(() => {
    if (open) {

      const seededAssets = clone(assets || []);
      seededAssets.forEach((asset: any, assetIndex: number) => {
        const options = Array.isArray(get(asset, 'data.options')) ? get(asset, 'data.options') : [];
        // const hasScanResult = options.some((option: any) => {
        //   const sr = get(option, 'data.scan_result', []);
        //   return Array.isArray(sr) && sr.length > 0;
        // });
        // if (options.length > 0 && !hasScanResult) {
        //   if (!asset.data) asset.data = {};
        //   asset.data.enabled = false;
        // }
        // Skip Scan should preserve the incoming enabled state.
        // if (failedAssetIndexSet.has(assetIndex)) {
        //   if (!asset.data) asset.data = {};
        //   asset.data.enabled = false;
        // }
        options.forEach((option: any) => {
          const componentNameKey = normalizeKey(get(option, 'component_name', get(option, 'label', '')));
          const schemaEntry = componentSchemaMap?.[componentNameKey] || {};
          const backendSchema =
            get(option, 'backend_schema') || get(schemaEntry, 'backend_schema') || get(option, 'panel.schema') || {};
          const messageProperties = get(backendSchema, 'properties.messages.items.properties', {});
          const propsProperties = get(messageProperties, 'props.items.properties', {});
          normalizeMessagesForOption(option, messageProperties, propsProperties);
        });
      });

      setDraftAssets(seededAssets);
      setSearch('');
      setCurrentPage(1);
      setSortState({ field: '', sort: '' });
      setExpandedRows([]);
      setExpandedMessageKeys({});
      setJsonFormErrorsByKey({});
      setFormSessionKey((prev) => prev + 1);
    } else {
      // Clear modal-local state on close to avoid stale draft reuse between import sessions.
      setDraftAssets([]);
      setExpandedRows([]);
      setExpandedMessageKeys({});
      setJsonFormErrorsByKey({});
    }
  }, [open, assets, componentSchemaMap, failedAssetIndexSet]);



  const replaceMessageObject = useCallback(
    (assetIndex: number, componentIndex: number, messageIndex: number, nextMessageData: Record<string, any>) => {
      setDraftAssets((prev) => {
        if (!Array.isArray(prev) || !prev[assetIndex]) return prev;

        const nextAssets = [...prev];
        const asset = { ...(nextAssets[assetIndex] || {}) };
        const assetData = { ...(asset.data || {}) };
        const options = Array.isArray(assetData.options) ? [...assetData.options] : [];
        const option = { ...(options[componentIndex] || {}) };
        const optionData = { ...(option.data || {}) };
        const messages = Array.isArray(optionData.messages) ? [...optionData.messages] : [];
        const currentMessage = messages[messageIndex] || {};
        const nextMessage = nextMessageData || {};

        if (isEqual(currentMessage, nextMessage)) {
          return prev;
        }

        messages[messageIndex] = { ...nextMessage };
        optionData.messages = messages;
        option.data = optionData;
        option.operation = get(option, 'operation', 'edited');
        options[componentIndex] = option;
        assetData.options = options;
        asset.data = assetData;
        nextAssets[assetIndex] = asset;

        return nextAssets;
      });
    },
    []
  );

  const updateAssetFieldForAsset = useCallback(
    (assetIndex: number, patch: Partial<Pick<any, 'name' | 'ip' | 'enabled'>>) => {
      setDraftAssets((prev) =>
        prev.map((asset, index) => {
          if (index !== assetIndex) return asset;

          const assetData = { ...(get(asset, 'data', {}) || {}) };
          let hasChange = false;

          if (patch.name !== undefined && assetData.name !== patch.name) {
            assetData.name = patch.name;
            hasChange = true;
          }

          if (patch.ip !== undefined && assetData.ip !== patch.ip) {
            assetData.ip = patch.ip;
            hasChange = true;
          }

          if (patch.enabled !== undefined && assetData.enabled !== patch.enabled) {
            assetData.enabled = patch.enabled;
            hasChange = true;
          }

          return hasChange ? { ...asset, data: assetData } : asset;
        })
      );
    },
    []
  );

  const updateComponentFieldForAsset = useCallback(
    (assetIndex: number, componentIndex: number, patch: Partial<Pick<any, 'enabled'>>) => {
      setDraftAssets((prev) =>
        prev.map((asset, index) => {
          if (index !== assetIndex) return asset;

          const assetData = { ...(get(asset, 'data', {}) || {}) };
          const options = Array.isArray(assetData.options) ? [...assetData.options] : [];
          const option = options[componentIndex];
          if (!option) return asset;

          const nextOption = { ...option };
          let hasChange = false;

          if (patch.enabled !== undefined && nextOption.enabled !== patch.enabled) {
            nextOption.enabled = patch.enabled;
            hasChange = true;
          }

          if (!hasChange) return asset;

          options[componentIndex] = nextOption;
          assetData.options = options;
          return { ...asset, data: assetData };
        })
      );
    },
    []
  );

  const updateSecretFieldForAsset = useCallback(
    (assetIndex: number, componentIndex: number, secretKey: string, value: string) => {
      setDraftAssets((prev) =>
        prev.map((asset, index) => {
          if (index !== assetIndex) return asset;

          const assetData = { ...(get(asset, 'data', {}) || {}) };
          const options = Array.isArray(assetData.options) ? [...assetData.options] : [];
          const option = options[componentIndex];
          if (!option) return asset;

          const optionData = { ...(get(option, 'data', {}) || {}) };
          if (String(optionData[secretKey] || '') === value) return asset;

          optionData[secretKey] = value;
          options[componentIndex] = { ...option, data: optionData };
          assetData.options = options;
          return { ...asset, data: assetData };
        })
      );
    },
    []
  );

  const deleteMessageObject = useCallback(
    (assetIndex: number, componentIndex: number, messageIndex: number) => {
      setDraftAssets((prev) => {
        if (!Array.isArray(prev) || !prev[assetIndex]) return prev;

        const nextAssets = [...prev];
        const asset = { ...(nextAssets[assetIndex] || {}) };
        const assetData = { ...(asset.data || {}) };
        const options = Array.isArray(assetData.options) ? [...assetData.options] : [];
        const option = { ...(options[componentIndex] || {}) };
        const optionData = { ...(option.data || {}) };
        const messages = Array.isArray(optionData.messages) ? [...optionData.messages] : [];

        if (!messages[messageIndex]) return prev;

        messages.splice(messageIndex, 1);
        optionData.messages = messages;
        option.data = optionData;
        option.operation = get(option, 'operation', 'edited');
        options[componentIndex] = option;
        assetData.options = options;
        asset.data = assetData;
        nextAssets[assetIndex] = asset;

        return nextAssets;
      });

      setExpandedMessageKeys({});

      // Simplest safe reset after message list mutation.
      setJsonFormErrorsByKey({});
    },
    []
  );

  const getComponentIssueWarning = useCallback(
    (row: MessageGridRow): string | null => {
      const componentName = normalizeText(row.componentName);
      const componentKey = normalizeKey(componentName);

      if (!componentName) {
        return 'Component is required.';
      }
      if (!componentSchemaMap?.[componentKey]) {
        return `Invalid component: "${componentName}".`;
      }
      return null;
    },
    [componentSchemaMap]
  );

  const secretIdToNameMap = useMemo(() => {
    const map = new Map<number, string>();
    (secretsData || []).forEach((secret: any) => {
      const id = Number(get(secret, 'secret_id'));
      const name = String(get(secret, 'secret_name', '')).trim();
      if (Number.isFinite(id) && name) {
        map.set(id, name);
      }
    });
    return map;
  }, [secretsData]);

  const secretNameToTokenMap = useMemo(() => {
    const map = new Map<string, string>();
    (secretsData || []).forEach((secret: any) => {
      const id = Number(get(secret, 'secret_id'));
      const name = String(get(secret, 'secret_name', '')).trim();
      if (Number.isFinite(id) && name) {
        map.set(name.toLowerCase(), `secret:${id}`);
      }
    });
    return map;
  }, [secretsData]);

  const resolveSecretDisplayValue = useCallback(
    (rawValue: string) => {
      const value = normalizeText(rawValue);
      const match = value.match(/^secret:(\d+)$/i);
      if (!match) return value;
      const secretId = Number(match[1]);
      const resolved = secretIdToNameMap.get(secretId);
      return resolved || value;
    },
    [secretIdToNameMap]
  );

  const getSecretFieldContext = useCallback(
    (row: MessageGridRow, secretField: SecretFieldDef) => {
      const componentKey = normalizeKey(row.componentName || '');
      const componentFields = componentSecretFieldsMap[componentKey] || [];
      const fieldConfig = componentFields.find((field) => field.key === secretField.key) || secretField;

      const rawValue = normalizeText(row.secrets?.[secretField.key] || '');
      const selectedDisplayValue = resolveSecretDisplayValue(rawValue);

      const configuredOptions = Array.from(
        new Set(
          [...(fieldConfig.options || [])]
            .map((option) => String(option).trim())
            .filter(Boolean)
        )
      );

      const isFieldApplicable = componentFields.some((field) => field.key === secretField.key) || Boolean(rawValue);
      const isDropdown = Boolean(fieldConfig.isDropdown) || configuredOptions.length > 0;
      const hasOptions = configuredOptions.length > 0;

      const hasInvalidDropdownValue =
        isFieldApplicable &&
        isDropdown &&
        hasOptions &&
        selectedDisplayValue.length > 0 &&
        !configuredOptions.some((option) => option.toLowerCase() === selectedDisplayValue.toLowerCase());

      return {
        isFieldApplicable,
        rawValue, // canonical value to persist, e.g. secret:1
        selectedDisplayValue, // UI value, e.g. MySecretName
        options: configuredOptions,
        isDropdown,
        hasOptions,
        hasInvalidDropdownValue,
      };
    },
    [componentSecretFieldsMap, resolveSecretDisplayValue]
  );

  const getSecretFieldHint = useCallback(
    (row: MessageGridRow, secretField: SecretFieldDef) => {
      const context = getSecretFieldContext(row, secretField);
      if (!context.isFieldApplicable) return '';
      if (context.isDropdown && !context.hasOptions) {
        if (context.rawValue) {
          return `Secret "${context.rawValue}" not configured.`;
        }
        return 'No secrets configured.';
      }
      if (context.hasInvalidDropdownValue) {
        return `Invalid secret: "${context.rawValue}".`;
      }
      return '';
    },
    [getSecretFieldContext]
  );

  const getRowRequiredSecretCoverage = useCallback(
    (row: MessageGridRow) => {
      const componentKey = normalizeKey(row.componentName || '');
      const schemaEntry = componentSchemaMap?.[componentKey] || {};
      const backendSchema = get(schemaEntry, 'backend_schema', {}) || {};

      const requiredSecretKeys = (Array.isArray(get(backendSchema, 'required_secrets', []))
        ? get(backendSchema, 'required_secrets', [])
        : []
      )
        .map((key: any) => String(key).trim())
        .filter(Boolean);

      if (!requiredSecretKeys.length) {
        return { applies: false, meets: true, missing: [] as string[] };
      }

      const selectedSecrets = Object.values(row.secrets || {})
        .map((value: any) => normalizeText(value))
        .filter(Boolean);

      const collectedKeys = new Set<string>();

      selectedSecrets.forEach((selectedSecret) => {
        const lowerSelected = selectedSecret.toLowerCase();

        let secret = (secretsData || []).find(
          (s: any) => String(get(s, 'secret_name', '')).trim().toLowerCase() === lowerSelected
        );

        if (!secret && lowerSelected.startsWith('secret:')) {
          const parsedId = Number(lowerSelected.split(':')[1]);
          if (!Number.isNaN(parsedId)) {
            secret = (secretsData || []).find((s: any) => Number(get(s, 'secret_id')) === parsedId);
          }
        }

        const keyList = Array.isArray(get(secret, 'secret_keys_list')) ? get(secret, 'secret_keys_list') : [];
        keyList.forEach((key: any) => collectedKeys.add(String(key).trim()));
      });

      const missing = requiredSecretKeys.filter((requiredKey: any) => !collectedKeys.has(requiredKey));
      return { applies: true, meets: missing.length === 0, missing };
    },
    [componentSchemaMap, secretsData]
  );

  const issues = useMemo(
    () =>
      collectMessageValidationIssues(draftAssets, componentSchemaMap).filter(
        (issue) => !failedAssetIndexSet.has(Number(issue.assetIndex))
      ),
    [draftAssets, componentSchemaMap, failedAssetIndexSet]
  );

  const issueErrorCount = issues.filter((issue) => issue.severity === 'error').length;
  const formErrorCount = useMemo(
    () => Object.values(jsonFormErrorsByKey).reduce((acc, errs) => acc + (Array.isArray(errs) ? errs.length : 0), 0),
    [jsonFormErrorsByKey]
  );
  const errorCount = issueErrorCount;

  const secretFieldDefs = useMemo(() => {
    const map = new Map<string, SecretFieldDef>();

    (draftAssets || []).forEach((asset: any, assetIndex: number) => {
      // if (failedAssetIndexSet.has(assetIndex)) return;

      const options = Array.isArray(get(asset, 'data.options')) ? get(asset, 'data.options') : [];
      options.forEach((option: any) => {
        const componentName = normalizeKey(get(option, 'component_name', get(option, 'label', '')));
        const dynamicFields = componentSecretFieldsMap[componentName] || [];
        dynamicFields.forEach((field) => {
          if (!map.has(field.key)) {
            map.set(field.key, field);
          }
        });

        const dataKeys = Object.keys(get(option, 'data', {}));
        dataKeys.forEach((key) => {
          if (!/^secret_/i.test(key)) return;
          if (!map.has(key)) {
            map.set(key, {
              key,
              label: toTitle(key.replace(/^secret_/i, '')),
              isDropdown: true,
              options: [],
            });
          }
        });
      });
    });

    return Array.from(map.values());
  }, [draftAssets, componentSecretFieldsMap, failedAssetIndexSet]);

  const gridRows = useMemo<MessageGridRow[]>(() => {
    const rows: MessageGridRow[] = [];

    (draftAssets || []).forEach((asset: any, assetIndex: number) => {
      // if (failedAssetIndexSet.has(assetIndex)) return;

      const assetName = normalizeText(get(asset, 'data.name', ''));
      const assetIp = normalizeText(get(asset, 'data.ip', ''));
      const assetStatus = get(asset, 'data.enabled', true) ? 'Enable' : 'Disable';
      const options = Array.isArray(get(asset, 'data.options')) ? get(asset, 'data.options') : [];

      if (!options.length) {
        rows.push({
          id: `asset-${assetIndex}-component-none`,
          assetIndex,
          componentIndex: -1,
          assetName,
          assetIp,
          assetStatus,
          componentName: '',
          componentStatus: '',
          secrets: {},
        });
        return;
      }

      options.forEach((option: any, componentIndex: number) => {
        const componentName = normalizeText(get(option, 'component_name', get(option, 'label', '')));
        const componentStatus = get(option, 'enabled', true) ? 'Enable' : 'Disable';
        const data = get(option, 'data', {});
        const componentKey = normalizeKey(componentName);

        const rowSecrets: Record<string, string> = {};
        (componentSecretFieldsMap[componentKey] || []).forEach((field) => {
          rowSecrets[field.key] = normalizeText(get(data, field.key, ''));
        });

        Object.keys(data || {}).forEach((key) => {
          if (!/^secret_/i.test(key)) return;
          if (!Object.prototype.hasOwnProperty.call(rowSecrets, key)) {
            rowSecrets[key] = normalizeText(get(data, key, ''));
          }
        });

        rows.push({
          id: `asset-${assetIndex}-component-${componentIndex}`,
          assetIndex,
          componentIndex,
          assetName,
          assetIp,
          assetStatus,
          componentName,
          componentStatus,
          secrets: rowSecrets,
        });
      });
    });

    return rows;
  }, [draftAssets, componentSecretFieldsMap, failedAssetIndexSet]);

  const uniqueAssetRows = useMemo(() => {
    const seen = new Set<number>();
    return gridRows.filter((row) => {
      if (seen.has(row.assetIndex)) return false;
      seen.add(row.assetIndex);
      return true;
    });
  }, [gridRows]);

  const assetNameCounts = useMemo(() => {
    const counts = new Map<string, number>();
    uniqueAssetRows.forEach((row) => {
      const name = normalizeText(row.assetName).toLowerCase();
      if (!name) return;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    });
    return counts;
  }, [uniqueAssetRows]);

  const assetIpCounts = useMemo(() => {
    const counts = new Map<string, number>();
    uniqueAssetRows.forEach((row) => {
      const ip = normalizeText(row.assetIp).toLowerCase();
      if (!ip) return;
      counts.set(ip, (counts.get(ip) ?? 0) + 1);
    });
    return counts;
  }, [uniqueAssetRows]);

  const getAssetNameHelperText = useCallback(
    (row: MessageGridRow) => {
      const value = normalizeText(row.assetName);
      const warningMessages: string[] = [];
      if (value && (assetNameCounts.get(value.toLowerCase()) ?? 0) > 1) {
        warningMessages.push(`Duplicate asset name: "${value}".`);
      }
      const rowIssueText = String(
        (validationRows || []).find(
          (validationRow: any) =>
            Number(validationRow?.asset_index) === Number(row.assetIndex) &&
            Number(validationRow?.component_index) === Number(row.componentIndex)
        )?.issues ||
        (validationRows || []).find(
          (validationRow: any) =>
            Number(validationRow?.asset_index) === Number(row.assetIndex) && Number(validationRow?.component_index) === -1
        )?.issues ||
        ''
      )
        .trim()
        .toLowerCase();

      if (
        rowIssueText.includes('duplicate with existing asset under selected gateway') ||
        rowIssueText.includes('asset already exists in gateway') ||
        rowIssueText.includes('asset already exists in the selected gateway')
      ) {
        warningMessages.push('Asset already exists in the selected gateway.');
      }

      if (rowIssueText.includes('duplicate with another imported asset') || rowIssueText.includes('duplicate asset in import')) {
        warningMessages.push('Duplicate asset in import.');
      }

      return {
        errorText: !value ? 'Asset name is required.' : '',
        warningText: warningMessages.join(' '),
      };
    },
    [assetNameCounts, validationRows]
  );

  const getAssetIpHelperText = useCallback(
    (row: MessageGridRow) => {
      const value = normalizeText(row.assetIp);
      const messages: string[] = [];
      if (!value) {
        messages.push('Asset IP is required.');
      }
      if (value && (assetIpCounts.get(value.toLowerCase()) ?? 0) > 1) {
        messages.push(`Duplicate IP: "${value}" (allowed).`);
      }
      return messages.join(' ');
    },
    [assetIpCounts]
  );

  const hasInlineValidationErrors = useMemo(() => {
    return gridRows.some((row) => {
      if (row.assetStatus === 'Disable') return false;
      const assetName = normalizeText(row.assetName);
      const assetIp = normalizeText(row.assetIp);
      const componentWarning = getComponentIssueWarning(row);

      // Secret warnings should not block Next
      return (
        !assetName ||
        !assetIp ||
        !normalizeText(row.assetStatus) ||
        !normalizeText(row.componentStatus) ||
        Boolean(componentWarning) ||
        (assetName && (assetNameCounts.get(assetName.toLowerCase()) ?? 0) > 1)
      );
    });
  }, [assetNameCounts, getComponentIssueWarning, gridRows]);

  // Code block to just show actual warning count
  // const duplicateAssetNameWarningCount = useMemo(() => {
  //   const duplicateNames = new Set<string>();

  //   uniqueAssetRows.forEach((row) => {
  //     const assetName = normalizeText(row.assetName).toLowerCase();
  //     if (!assetName) return;
  //     if ((assetNameCounts.get(assetName) ?? 0) > 1) {
  //       duplicateNames.add(assetName);
  //     }
  //   });

  //   return duplicateNames.size;
  // }, [assetNameCounts, uniqueAssetRows]);

  // const warningCount = useMemo(
  //   () => issues.filter((issue) => issue.severity === 'warning').length + duplicateAssetNameWarningCount,
  //   [duplicateAssetNameWarningCount, issues]
  // );


  // code block to calculate all warning count
  const inlineAssetWarningCount = useMemo(() => {
    const warningKeys = new Set<string>();

    gridRows.forEach((row) => {
      // Asset-name level warnings (includes gateway/external duplicate messages from validationRows)
      const assetNameHelper = getAssetNameHelperText(row);
      if (!assetNameHelper.errorText && assetNameHelper.warningText) {
        const assetNameKey = normalizeText(row.assetName).toLowerCase() || String(row.assetIndex);

        if (assetNameHelper.warningText.includes('Duplicate asset name')) {
          warningKeys.add(`asset-name-duplicate::${assetNameKey}`);
        }
        if (assetNameHelper.warningText.includes('Asset already exists in the selected gateway.')) {
          warningKeys.add(`asset-exists-in-gateway::${row.assetIndex}`);
        }
        if (assetNameHelper.warningText.includes('Duplicate asset in import.')) {
          warningKeys.add(`asset-duplicate-in-import::${row.assetIndex}`);
        }
        if (failedAssetIndexSet.has(Number(row.assetIndex)) && row.assetStatus === 'Disable') {
          warningKeys.add(`asset-scan-auto-disabled::${row.assetIndex}`);
        }
      }

      // Duplicate IP warning (allowed)
      const assetIpKey = normalizeText(row.assetIp).toLowerCase();
      if (assetIpKey && (assetIpCounts.get(assetIpKey) ?? 0) > 1) {
        warningKeys.add(`asset-ip-duplicate::${assetIpKey}`);
      }

      // Auto-disabled due to scan failure warning
      const asset = draftAssets[row.assetIndex];
      const assetOptions = Array.isArray(get(asset, 'data.options')) ? get(asset, 'data.options') : [];
      const missesScanResult =
        assetOptions.length > 0 &&
        !assetOptions.some((opt: any) => {
          const sr = get(opt, 'data.scan_result', []);
          return Array.isArray(sr) && sr.length > 0;
        });

      if (failedAssetIndexSet.has(Number(row.assetIndex)) && missesScanResult && row.assetStatus === 'Disable') {
        warningKeys.add(`asset-scan-auto-disabled::${row.assetIndex}`);
      }

      // Invalid component warning (non-empty but schema not found)
      const componentName = normalizeText(row.componentName);
      const componentKey = normalizeKey(componentName);
      if (componentName && !componentSchemaMap?.[componentKey]) {
        warningKeys.add(`component-invalid::${row.assetIndex}::${row.componentIndex}`);
      }

      // Secret warnings (invalid secret / required secret keys missing)
      secretFieldDefs.forEach((field) => {
        const context = getSecretFieldContext(row, field);
        const requiredCoverage = getRowRequiredSecretCoverage(row);

        const hasRequiredCoverageWarning =
          requiredCoverage.applies && !requiredCoverage.meets && context.isFieldApplicable;

        const hasInvalidDropdownWarning = context.hasInvalidDropdownValue;
        const hasValidSelectedSecret =
          context.isDropdown &&
          context.isFieldApplicable &&
          context.hasOptions &&
          Boolean(context.selectedDisplayValue) &&
          !hasInvalidDropdownWarning;

        const showRequiredCoverageWarning = hasRequiredCoverageWarning && !hasValidSelectedSecret;
        const showSecretWarning = hasInvalidDropdownWarning || showRequiredCoverageWarning;

        if (showSecretWarning) {
          warningKeys.add(`secret-warning::${row.assetIndex}::${row.componentIndex}::${field.key}`);
        }
      });
    });

    return warningKeys.size;
  }, [
    gridRows,
    getAssetNameHelperText,
    assetIpCounts,
    draftAssets,
    componentSchemaMap,
    secretFieldDefs,
    getSecretFieldContext,
    getRowRequiredSecretCoverage,
  ]);

  const warningCount = useMemo(
    () => issues.filter((issue) => issue.severity === 'warning').length + inlineAssetWarningCount,
    [issues, inlineAssetWarningCount]
  );

  const assetsWithMessageErrors = useMemo(() => {
    const indexes = new Set<number>();
    issues.forEach((issue) => {
      if (issue.severity === 'error') {
        indexes.add(Number(issue.assetIndex));
      }
    });
    return indexes;
  }, [issues]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return gridRows;

    return gridRows.filter((row) => {
      const componentIssues = issues
        .filter((issue) => issue.assetIndex === row.assetIndex && issue.componentIndex === row.componentIndex)
        .map((issue) => issue.text)
        .join(' ')
        .toLowerCase();

      const secretValues = Object.values(row.secrets || {}).join(' ').toLowerCase();

      return [
        row.assetName,
        row.assetIp,
        row.assetStatus,
        row.componentName,
        row.componentStatus,
        secretValues,
        componentIssues,
      ]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [gridRows, issues, search]);

  const sortedRows = useMemo(() => {
    if (!sortState.field || !sortState.sort) return filteredRows;

    const sortableFields = new Set(['assetName', 'assetIp', 'assetStatus', 'componentStatus']);
    if (!sortableFields.has(sortState.field)) return filteredRows;

    const direction = sortState.sort === 'asc' ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const aValue = String((a as any)[sortState.field] ?? '').trim();
      const bValue = String((b as any)[sortState.field] ?? '').trim();
      return direction * aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [filteredRows, sortState]);


  const hideFieldsFromUiSchema = (uiSchemaInput: any, hiddenFieldKeys: string[]) => {
    const nextUiSchema = cloneDeep(uiSchemaInput || {});
    const hidden = new Set((hiddenFieldKeys || []).map((k) => String(k).trim()).filter(Boolean));

    const prune = (node: any): any => {
      if (!node || typeof node !== 'object') return node;

      const nextNode = { ...node };

      if (nextNode.type === 'Control') {
        const key = scopeToKey(get(nextNode, 'scope', ''));
        if (hidden.has(key)) return null;
      }

      if (Array.isArray(nextNode.elements)) {
        nextNode.elements = nextNode.elements.map(prune).filter(Boolean);
      }

      if (Array.isArray(nextNode.options?.detail?.elements)) {
        nextNode.options = {
          ...(nextNode.options || {}),
          detail: {
            ...(nextNode.options?.detail || {}),
            elements: nextNode.options.detail.elements.map(prune).filter(Boolean),
          },
        };
      }

      return nextNode;
    };

    return prune(nextUiSchema) || { type: 'VerticalLayout', elements: [] };
  };
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedRows.slice(start, start + PAGE_SIZE).map((row) => {
      const componentIssues = issues.filter(
        (issue) => issue.assetIndex === row.assetIndex && issue.componentIndex === row.componentIndex
      );
      const option = row.componentIndex >= 0 ? draftAssets?.[row.assetIndex]?.data?.options?.[row.componentIndex] : null;
      const componentNameKey = normalizeKey(row.componentName || get(option, 'component_name', get(option, 'label', '')));
      const componentSchemaEntry = componentSchemaMap?.[componentNameKey] || {};
      const rawSchema =
        get(option, 'backend_schema') || get(componentSchemaEntry, 'backend_schema') || get(option, 'panel.schema') || {};
      const rawUiSchema =
        get(option, 'ui_schema') || get(componentSchemaEntry, 'ui_schema') || get(option, 'panel.uiSchema') || {};
      const scanResult = get(option, 'data.scan_result', []);
      const jsonSchema = applyScanResultToSchema(rawSchema, scanResult);
      const jsonUiSchema = cloneDeep(rawUiSchema || {});

      const messageItemSchema = get(jsonSchema, 'properties.messages.items', {});
      const messageItemSchemaReadonly = cloneDeep(messageItemSchema || {});
      ['id', 'name', 'type'].forEach((fieldKey) => {
        if (messageItemSchemaReadonly?.properties?.[fieldKey]) {
          messageItemSchemaReadonly.properties[fieldKey].readOnly = true;
        }
      });
      const messageProperties = get(messageItemSchema, 'properties', {});
      const messagesControl = getMessagesControl(jsonUiSchema);
      const messageLabels = getMessageLabels(messagesControl);
      const messagePropertiesReadonly = get(messageItemSchemaReadonly, 'properties', {});
      const hiddenFieldKeys = Object.entries(messagePropertiesReadonly)
        .filter(([, schemaDef]: any) => get(schemaDef, 'x-hideInMessageValidationUi') === true)
        .map(([key]) => key);

      const messageDetailUiSchema = hideFieldsFromUiSchema(
        applyReadonlyToMessageHeaderFields(
          cloneDeep(get(messagesControl, 'options.detail', { type: 'VerticalLayout', elements: [] }))
        ),
        hiddenFieldKeys
      );
      const messages = Array.isArray(get(option, 'data.messages')) ? get(option, 'data.messages') : [];

      const expandable = (
        <Box sx={{ border: '0px solid #D9DFED80', borderRadius: '0px', overflow: 'hidden', m: 1.25 }}>
          {(!row.assetName || !row.assetIp) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, bgcolor: '#FFF7F7', borderBottom: '1px solid #F8D7DA' }}>
              <WarningAmberRoundedIcon sx={{ color: '#D81010', fontSize: 16 }} />
              <Typography variant='caption' sx={{ color: '#D81010' }}>
                Invalid Asset
              </Typography>
            </Box>
          )}

          {row.componentIndex >= 0 ? (
            <Box sx={{ p: 0 }}>
              {!jsonSchema || Object.keys(jsonSchema).length === 0 ? (
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant='caption' sx={{ color: '#64759F' }}>
                    Component schema is missing for this row.
                  </Typography>
                </Box>
              ) : messages.length === 0 ? (
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant='caption' sx={{ color: '#64759F' }}>
                    No messages found.
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {messages.map((message: any, messageIndex: number) => {
                    const panelKey = `${String(row.id)}::message-panel::${messageIndex}`;
                    const isPanelExpanded = expandedMessageKeys[panelKey] ?? true;
                    const messageErrorKey = `${String(row.id)}::message::${messageIndex}`;
                    const messageIssues = componentIssues.filter((issue) => issue.messageIndex === messageIndex);
                    const hasActionableMessageErrors = messageIssues.some(
                      (issue) =>
                        issue.code === 'read_tag_not_found_in_scan' ||
                        issue.code === 'parameter_id_not_found_in_scan' ||
                        issue.code === 'duplicate_message' ||
                        issue.code === 'message_name_blank'
                    );
                    const messageFormData = message || {};

                    return (
                      <Accordion
                        key={`${row.id}-message-${messageIndex}`}
                        expanded={isPanelExpanded}
                        onChange={(_e, expanded) => {
                          setExpandedMessageKeys((prev) => ({
                            ...prev,
                            [panelKey]: expanded,
                          }));
                        }}
                        sx={{
                          border: hasActionableMessageErrors ? '1px solid #F5C2C7' : '1px solid #D9DFED80',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          mb: 1.5,
                          backgroundColor: '#fff',
                          boxShadow: 'none',
                          '&::before': { display: 'none' },
                          '&.Mui-expanded': { mt: 0, mb: 1.5 },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={isPanelExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          sx={{
                            px: 2,
                            py: 0.75,
                            minHeight: '40px !important',
                            backgroundColor: hasActionableMessageErrors ? '#FFF5F5' : '#FFFFF',
                            borderBottom: hasActionableMessageErrors ? '1px solid #F8D7DA' : '1px solid #EEF2FB',
                            '& .MuiAccordionSummary-content': {
                              margin: '0 !important',
                              display: 'flex',
                              gap: 3,
                              flexWrap: 'wrap',
                              alignItems: 'center',
                            },
                          }}
                        >
                          {hasActionableMessageErrors && (
                            <AlertIcon style={{ color: '#D81010' }} />
                          )}
                          <Typography variant='caption' sx={{ color: '#434D67', fontSize: '1rem', fontWeight: 400 }}>
                            {messageLabels.id || messageLabels.name || 'Message Name'} :{' '}
                            <b style={{ fontWeight: 600 }}>{String(get(message, 'id', get(message, 'name', `Message ${messageIndex + 1}`)))}</b>
                          </Typography>
                          <Typography variant='caption' sx={{ color: '#434D67', fontSize: '1rem', fontWeight: 400 }}>
                            {messageLabels.type || 'Message Type'} :{' '}
                            <b style={{ fontWeight: 600 }}>{getSchemaDisplayValue(get(message, 'type', '-'), get(messageProperties, 'type', {}))}</b>
                          </Typography>
                          <IconButton
                            size='small'
                            color='error'
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setPendingDeleteMessage({ assetIndex: row.assetIndex, componentIndex: row.componentIndex, messageIndex });
                            }}
                            sx={{ ml: 'auto', mr: 2 }}
                          >
                            <DeleteOutlineRoundedIcon fontSize='small' color='#787774' />
                          </IconButton>
                        </AccordionSummary>

                        <AccordionDetails sx={{ p: 0, borderTop: 'none' }}>
                          <Box sx={{ px: 3, py: 2 }} className='jsonFormsWrapper'>
                            <PropIssuesContext.Provider value={messageIssues}>
                              <JsonFormsWrapper
                                key={`msg-form-${formSessionKey}-${row.id}-${messageIndex}`}
                                schema={messageItemSchemaReadonly}
                                uiSchema={messageDetailUiSchema}
                                data={messageFormData}
                                readOnly={row.assetStatus === 'Disable'}
                                includeFlowDependentRenderers={false}
                                onChange={({ data: formData, errors }: any) => {
                                  const nextFormData = formData || {};
                                  const nextErrors = uniqBy(
                                    [...(Array.isArray(errors) ? errors : [])],
                                    (entry: any) => `${entry.instancePath || ''}::${entry.keyword || ''}::${entry.message || ''}`
                                  );

                                  const mergedFormData = {
                                    ...(messageFormData || {}),
                                    ...nextFormData,
                                  };

                                  replaceMessageObject(
                                    row.assetIndex,
                                    row.componentIndex,
                                    messageIndex,
                                    mergedFormData
                                  );

                                  setJsonFormErrorsByKey((prev) => {
                                    const currentErrors = Array.isArray(prev[messageErrorKey]) ? prev[messageErrorKey] : [];
                                    if (isEqual(currentErrors, nextErrors)) {
                                      return prev;
                                    }

                                    return {
                                      ...prev,
                                      [messageErrorKey]: nextErrors,
                                    };
                                  });
                                }}
                                hasErrors={(jsonFormErrorsByKey[messageErrorKey] || []).length > 0 || hasActionableMessageErrors}
                              />
                            </PropIssuesContext.Provider>
                          </Box>

                          {messageIssues
                            .filter((issue) => !issue.editableField)
                            .map((issue: MessageIssue) => (
                              <Box key={issue.id} sx={{ px: 2, py: 0.6, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <ErrorOutlineRoundedIcon
                                  sx={{
                                    color: issue.severity === 'error' ? '#D81010' : '#F59E0B',
                                    fontSize: 15,
                                    mt: '1px',
                                  }}
                                />
                                <Typography
                                  variant='caption'
                                  sx={{ color: issue.severity === 'error' ? '#D81010' : '#78350F' }}
                                >
                                  {issue.text}
                                </Typography>
                              </Box>
                            ))}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              )}

              {componentIssues
                .filter((issue) => !issue.editableField && issue.code !== 'no_messages')
                .map((issue: MessageIssue) => (
                  <Box key={issue.id} sx={{ px: 0.5, py: 0.25, marginTop: '16px', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <ErrorOutlineRoundedIcon
                      sx={{
                        color: issue.severity === 'error' ? '#D81010' : '#F59E0B',
                        fontSize: 15,
                        mt: '1px',
                      }}
                    />
                    <Typography
                      variant='caption'
                      sx={{ color: issue.severity === 'error' ? '#D81010' : '#78350F' }}
                    >
                      {issue.text}
                    </Typography>
                  </Box>
                ))}

            </Box>
          ) : (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant='caption' sx={{ color: '#64759F' }}>
                No component available for this asset.
              </Typography>
            </Box>
          )}
        </Box>
      );

      return {
        ...row,
        hasMessageValidationError: assetsWithMessageErrors.has(Number(row.assetIndex)),
        accordianComponent: expandable,
      };
    });
  }, [
    sortedRows,
    currentPage,
    issues,
    expandedMessageKeys,
    draftAssets,
    componentSchemaMap,
    PAGE_SIZE,
    jsonFormErrorsByKey,
    formSessionKey,
    replaceMessageObject,
    deleteMessageObject,
    assetsWithMessageErrors,
  ]);

  const toggleRow = (rowId: string | number) => {
    setExpandedRows((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [rowId]));
  };

  const secretColumns = useMemo(() => {
    return secretFieldDefs.map((field) => ({
      field: `secret_${field.key}`,
      headerName: noWrapHeader(`${String(field.label).toUpperCase()}`),
      headerAlign: 'left',
      minWidth: 200,
      maxWidth: 300,
      sortable: false,
      renderCell: ({ row }: any) => {
        const context = getSecretFieldContext(row, field);
        const requiredCoverage = getRowRequiredSecretCoverage(row);
        const hasRequiredCoverageWarning =
          requiredCoverage.applies && !requiredCoverage.meets && context.isFieldApplicable;

        const hasInvalidDropdownWarning = context.hasInvalidDropdownValue;
        const hasValidSelectedSecret =
          context.isDropdown &&
          context.isFieldApplicable &&
          context.hasOptions &&
          Boolean(context.selectedDisplayValue) &&
          !hasInvalidDropdownWarning;
        const showRequiredCoverageWarning = hasRequiredCoverageWarning && !hasValidSelectedSecret;

        const baseHint = getSecretFieldHint(row, field);
        const secretHint = hasInvalidDropdownWarning
          ? baseHint
          : showRequiredCoverageWarning
            ? 'Missing keys: ' + requiredCoverage.missing.join(', ') + '.'
            : baseHint;

        const showSecretWarning = hasInvalidDropdownWarning || showRequiredCoverageWarning;

        const selectOptions = context.options.length > 0
          ? context.options
          : context.selectedDisplayValue
            ? [context.selectedDisplayValue]
            : [];

        return context.isDropdown ? (
          <ValidationSelectCell
            value={context.selectedDisplayValue}
            options={selectOptions.map((option) => ({ label: option, value: option }))}
            placeholder={!context.isFieldApplicable ? 'N/A' : !context.hasOptions ? 'No secrets available' : 'Select Secret'}
            disabled={!context.isFieldApplicable || !context.hasOptions || row.assetStatus === 'Disable'}
            error={false}
            warning={showSecretWarning}
            helperText={secretHint || ' '}
            helperError={false}
            onChange={(value) => {
              const normalized = normalizeText(value);
              const canonicalValue = secretNameToTokenMap.get(normalized.toLowerCase()) || normalized;
              updateSecretFieldForAsset(row.assetIndex, row.componentIndex, field.key, canonicalValue);
            }}
          />
        ) : (
          <ValidationTextCell
            value={context.rawValue}
            disabled={!context.isFieldApplicable || row.assetStatus === 'Disable'}
            placeholder={context.isFieldApplicable ? '' : 'N/A'}
            warning={showSecretWarning}
            helperText={secretHint || ' '}
            helperError={false}
            onChange={(value) => updateSecretFieldForAsset(row.assetIndex, row.componentIndex, field.key, value)}
          />
        );
      }
    }));
  }, [
    getSecretFieldContext,
    getSecretFieldHint,
    getRowRequiredSecretCoverage,
    secretFieldDefs,
    updateSecretFieldForAsset,
    secretNameToTokenMap,
  ]);
  const tableColumns = useMemo(
    () => [
      {
        field: 'assetName',
        headerName: noWrapHeader('ASSET NAME'),
        headerAlign: 'left',
        sortable: true,
        minWidth: 150,
        renderCell: ({ row }: any) => {
          const helper = getAssetNameHelperText(row);
          const hasError = Boolean(helper.errorText.trim());
          const helperText = helper.errorText || helper.warningText || ' ';
          const hasWarning = !hasError && Boolean(helper.warningText);


          return (
            <ValidationTextCell
              value={row.assetName || ''}
              error={hasError}
              warning={hasWarning}
              helperText={helperText}
              helperError={hasError}
              disabled={row.assetStatus === 'Disable'}
              onChange={(value) => updateAssetFieldForAsset(row.assetIndex, { name: value })}
            />
          );
        },
      },
      {
        field: 'assetIp',
        headerName: noWrapHeader('ASSET IP'),
        headerAlign: 'left',
        sortable: true,
        minWidth: 150,
        renderCell: ({ row }: any) => {
          const helperText = getAssetIpHelperText(row);
          const hasError = !normalizeText(row.assetIp);
          const hasWarning = !hasError && Boolean(helperText);

          return (
            <ValidationTextCell
              value={row.assetIp || ''}
              error={hasError}
              warning={hasWarning}
              helperText={helperText}
              helperError={hasError}
              disabled={row.assetStatus === 'Disable'}
              onChange={(value) => updateAssetFieldForAsset(row.assetIndex, { ip: value })}
            />
          );
        },
      },
      {
        field: 'assetStatus',
        headerName: noWrapHeader('ASSET STATUS'),
        headerAlign: 'left',
        sortable: true,
        width: 70,
        maxWidth: 150,
        renderCell: ({ row }: any) => {
          const hasError = !normalizeText(row.assetStatus);
          const isScanFailedAsset = failedAssetIndexSet.has(Number(row.assetIndex));

          const helperText = hasError
            ? 'Asset status is required.'
            : isScanFailedAsset && row.assetStatus === 'Disable'
              ? 'Auto-disabled: Scan Failed.'
              : ' ';

          const hasWarning = !hasError && isScanFailedAsset && row.assetStatus === 'Disable';


          return (
            <ValidationSelectCell
              value={row.assetStatus || ''}
              options={STATUS_OPTIONS}
              placeholder='Select Status'
              error={hasError}
              warning={hasWarning}
              helperText={helperText}
              helperError={hasError}
              onChange={(value) => updateAssetFieldForAsset(row.assetIndex, { enabled: value === 'Enable' })}
            />
          )
        },
      },
      {
        field: 'componentName',
        headerName: noWrapHeader('COMPONENT NAME'),
        headerAlign: 'left',
        sortable: false,
        minWidth: 150,
        renderCell: ({ row }: any) => {
          const componentHint = getComponentIssueWarning(row) || '';
          const hasError = !normalizeText(row.componentName);
          const hasWarning = !hasError && Boolean(componentHint);
          return (
            <ValidationTextCell
              value={row.componentName || ''}
              disabled
              error={hasError}
              warning={hasWarning}
              helperText={componentHint || (hasError ? 'Component is required.' : ' ')}
              helperError={hasError}
              onChange={() => { }}
            />
          )
        },
      },
      {
        field: 'componentStatus',
        headerName: noWrapHeader('COMPONENT STATUS'),
        headerAlign: 'left',
        sortable: true,
        width: 70,
        maxWidth: 150,
        renderCell: ({ row }: any) => {
          const hasError = !normalizeText(row.componentStatus);
          const helperText = hasError ? 'Component status is required.' : ' ';
          return (
            <ValidationSelectCell
              value={row.componentStatus || ''}
              options={STATUS_OPTIONS}
              placeholder='Select Status'
              error={hasError}
              warning={false}
              helperText={helperText}
              helperError={hasError}
              onChange={(value) => updateComponentFieldForAsset(row.assetIndex, row.componentIndex, { enabled: value === 'Enable' })}
            />
          )

        },
      },
      ...secretColumns,
      {
        field: 'expand',
        headerName: '',
        width: 40,
        align: 'right',
        sortable: false,
        renderCell: ({ row }: any) => (
          <IconButton size='small' sx={{ marginTop: '-20px' }} onClick={() => toggleRow(row.id)}>
            {expandedRows.includes(row.id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        ),
      },
    ],
    [
      expandedRows,
      getAssetIpHelperText,
      getAssetNameHelperText,
      getComponentIssueWarning,
      getSecretFieldContext,
      getSecretFieldHint,
      secretColumns,
      updateAssetFieldForAsset,
      updateComponentFieldForAsset,
      updateSecretFieldForAsset,
      draftAssets,
    ]
  );

  const ErrorBox = ({ errorType, errorCount }: { errorType: string; errorCount: number }) => (
    <Box sx={{ display: 'flex', gap: '8px !important', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', height: '32px', width: '32px', backgroundColor: errorType === 'error' ? '#FFEBEE' : '#FFF8E1', borderRadius: '4px', border: `1px solid ${errorType === 'error' ? '#D32F2F' : '#FFA000'}`, justifyContent: 'center', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '16px', color: errorType === 'error' ? '#D32F2F' : '#FFA000' }}>{String(errorCount).padStart(2, '0')}</Typography>
      </Box>
      <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>{errorType === 'error' ? 'Error(s)' : 'Warning(s)'}</Typography>
    </Box>
  );

  const isSaveDisabled = errorCount > 0 || hasInlineValidationErrors;

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            ...Styles.dialog.muiPaper,
            maxWidth: '100%',
            width: '100vw',
            minHeight: '98vh',
            maxHeight: '98vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.background.default,
            margin: '12px',
          },
        }}
      >
        <DialogTitle sx={{ px: 4, py: 3, borderBottom: '1px solid #D9DFED80' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant='subtitle1' sx={{ ...Styles.dialogTitle, padding: 0, color: theme.palette.text.primary }}>
                List of Assets
              </Typography>
              <Typography variant='caption' sx={{ color: theme.palette.text.secondary, mt: 0, display: 'block' }}>
                Review and correct the message tag assignments for each asset before proceeding.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <ErrorBox errorType='error' errorCount={errorCount} />
                <ErrorBox errorType='warning' errorCount={warningCount} />
              </Box>
              <SearchField
                searchPlaceholder='Search by Asset Name / IP'
                defaultValue={search}
                onSearch={(val: string) => setSearch(val)}
                width='300px'
              />
            </Box>
          </Box>
        </DialogTitle>



        <DialogContent
          sx={{
            '&.MuiDialogContent-root': {
              display: 'flex',
              flexDirection: 'column',
              padding: '0px !important',
              flex: 1,
              overflow: 'hidden',
              minHeight: 0,
            },
          }}
        >
          {/* Table — flex: 1 fills remaining dialog height; overflow: auto makes table+pagination scroll within this box */}
          <Box
            sx={{
              width: '100%',
              position: 'relative',
              overflow: 'auto',
              px: 0,
            }}
          >
            <CustomDataTable
              rows={pagedRows}
              columns={tableColumns}
              tableHeight='calc(100vh - 285px)'
              rowHeight={TABLE_ROW_HEIGHT}
              isExpandable
              expandedRows={expandedRows}
              isServerPagination
              totalRowCount={sortedRows.length}
              rowsPerPage={PAGE_SIZE}
              page={currentPage}
              onPagination={(_rowsPerPage: number, page: number) => {
                setCurrentPage(page);
                setExpandedRows([]);
              }}
              onSorting={(sortModel: SortState) => {
                setSortState({ field: sortModel.field || '', sort: sortModel.sort || '' });
                setCurrentPage(1);
                setExpandedRows([]);
              }}
              noRowsLabel='No assets found.'
              getRowId={(row: any) => row.id}
              getRowSx={(row: any) => {
                if (!row?.hasMessageValidationError) return {};
                return {
                  '.MuiTableCell-root': {
                    backgroundColor: '#F614141A',
                  },
                };
              }}
              isFromBulkAssetsPopup
            />
          </Box>

          <Box sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            pt: 2,
            pb: 3,
            px: 0,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            flexShrink: 0,
            mt: 'auto',
            borderTop: '1px solid #D9DFED80'
          }}>
            <Button variant='outlined' onClick={onClose}>
              Cancel
            </Button>
            <Button variant='contained' onClick={() => onContinue(draftAssets)} disabled={isSaveDisabled}>
              Next
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <PopUp
        open={!!pendingDeleteMessage}
        icon={<CrossIcon color={palette.warning.main} />}
        message='Are you sure you want to delete this message?'
        cancel
        cancelLabel='No'
        confirmation
        confirmLabel='Yes'
        onCancel={() => setPendingDeleteMessage(null)}
        onClose={() => setPendingDeleteMessage(null)}
        onConfirm={() => {
          if (pendingDeleteMessage) {
            deleteMessageObject(
              pendingDeleteMessage.assetIndex,
              pendingDeleteMessage.componentIndex,
              pendingDeleteMessage.messageIndex
            );
          }
          setPendingDeleteMessage(null);
        }}
      />
    </>
  );
};

export default memo(FlowImportMessageValidationModal);
